/**
 * 保险内核 —— 保单口径的**结构性判定唯一实现**（**B 域 · B5**）
 *
 * 依据：B 域 PRD **§5.5.5**（保险三分类 + 保单台账）· `DR-008` §6.1/§7（保费落腿模型 ·
 * `insurance_sav` 挂账 vs 现金价值分列不合并）· `DR-016` **§6.9**（保单状态扩充为 6 态）·
 * `DR-016` §7（理赔冲减）· 架构 **§5.4**（净资产判定表）。
 *
 * ## 🔴 本文件只回答三件事
 *
 * 1. **一个承保状态下，现金价值算不算资产**（{@link cashValueCountsInNetWorth}）——
 *    这个判定的**唯一实现**；`composeNetWorth` 的调用方**不得**各自写
 *    `status === 'active' || status === 'paid_up'`（那就是铁律 24 点名的双实现）；
 * 2. **保单形态是否合法**（{@link checkPolicyShape}）—— 必填项与金额边界；
 * 3. **「投保人 / 被保人 / 受益人」这个文本列到底存了什么**（{@link resolveParty}）。
 *
 * ## ⚠️ 本文件**不**负责什么
 *
 * - **净资产的加总**：那是 `nucleus/networth.ts` 的 `composeNetWorth`（唯一权威纯函数）；
 *   本文件只提供"**哪些保单的现金价值该进表**"这一个判定，供其调用方喂数据；
 * - **保费的收支归属**：储蓄型保费 `excludeFromStats=1`（资产转化）、消费型进「保费支出」——
 *   那是**腿的标记**，由记一笔/工资条落地（C 域），本文件不管；
 * - **保额**：🔴 **一律不计净资产**，只作展示（B 域 PRD §5.5.5）。
 */

import { PolicyStatus, InternalCategory } from '../enums/account.js';
import type { LegDirection } from '../enums/journal.js';
// 🔴 社保可用型的**权威集合**在净资产判定表里（`networth.ts`）—— 本文件**派生**侧别，不另列清单
import { SOCIAL_AVAILABLE, SOCIAL_EXCLUDED } from './networth.js';

// ═══════════════════════ 1. 现金价值是否入净资产 ═══════════════════════

/**
 * 该承保状态下，**现金价值是否计入净资产**（B 域 PRD §5.5.5 · `DR-016` §6.9）。
 *
 * | 状态 | 计入 | 理由 |
 * |---|:--:|---|
 * | `active` | ✅ | 保障中，是有价值的存量 |
 * | `paid_up` | ✅ | 已缴清但保障继续，同属有价值的存量 |
 * | `lapsed` | ❌ | 已失效 —— 计入会让净资产虚增 |
 * | `claimed` | ❌ | 已给付结算 |
 * | `surrendered` | ❌ | `DR-016` §6.9：现金价值**归零** |
 * | `matured` | ❌ | 同上（给付走「转账 + 差额不计收支」） |
 *
 * 🔴 **为什么必须是一处**：若前端按一版、后端按另一版，症状是**保单价值重复计入或漏计**
 * （B 域 PRD §9 风险 6 已点名 "前后端各按一版 ⇒ 净值虚增"）。
 */
export function cashValueCountsInNetWorth(status: PolicyStatus): boolean {
  return status === PolicyStatus.Active || status === PolicyStatus.PaidUp;
}

// ═══════════════════════ 2. 三人身份：id 优先、姓名兜底 ═══════════════════════

/**
 * 系统用户 id 的形态（`usr_` + base36）。
 *
 * ⚠️ **与 `server/src/common/ids.ts` 的 `IdPrefix.User` 必须一致** —— 这里是**共享层**，
 * 不能 import 服务端常量（跨层依赖方向），故以字面量形态判定。
 * 🔴 改前缀时**两处都要改**（已在 `enums.spec` / `insurance.spec` 留断言）。
 */
const USER_ID_PATTERN = /^usr_[0-9a-z]{1,32}$/;

/** 一个「保单当事人」字段里到底存了什么 */
export type PartyRef =
  | { kind: 'user'; userId: string }
  | { kind: 'name'; name: string }
  | { kind: 'empty' };

/**
 * 判定「投保人 / 被保人 / 受益人」列里存的是**系统用户 id** 还是**姓名文本**。
 *
 * 🔴 **口径 = 「id 优先、姓名兜底」**（2026-09-30 拍板）：家庭场景里当事人**可能不是系统用户**
 * （孩子 / 父母未注册），故列类型保持 `text` 不变；填了系统用户 id 就按 id 解析
 * （可 join 出姓名、可做"我的保单"筛选），否则一律按**姓名**展示。
 *
 * ⚠️ **已知歧义（诚实登记）**：若某人**恰好把姓名填成 `usr_` 开头的形态**，会被误判为用户 id。
 * 该风险**选择接受**而不是加第二个列：加列 = 同一事实两份载体（本项目已因同型问题踩坑多次），
 * 而真实姓名以 `usr_` 开头且正好是合法 base36 的概率可忽略。
 */
export function resolveParty(value: string | null | undefined): PartyRef {
  if (value === null || value === undefined) return { kind: 'empty' };
  const trimmed = value.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (USER_ID_PATTERN.test(trimmed)) return { kind: 'user', userId: trimmed };
  return { kind: 'name', name: trimmed };
}

// ═══════════════════════ 3. 保单形态校验 ═══════════════════════

/** 保单形态判定的输入（**最小子集**，与 `insurance_policies` 的列对应） */
export interface PolicyShapeInput {
  policyNo: string;
  company: string;
  insuranceType: string;
  /** `YYYY-MM-DD`；缺省 = 未填 */
  startDate?: string | null;
  endDate?: string | null;
  /** 保额（整数分）；缺省 = 未填。🔴 **不计净资产** */
  sumAssured?: number | null;
  /** 现金价值（整数分）；缺省 = 未填。🔴 **入净资产**（限 `active`/`paid_up`） */
  cashValue?: number | null;
  /** 年缴保费（整数分）；缺省 = 未填 */
  annualPremium?: number | null;
  status: PolicyStatus;
}

/**
 * 保单形态校验 —— 返回**问题清单**（空数组 = 通过）。
 *
 * ## 🔴 覆盖范围（订正于 2026-10-03 · ㉝ 批）
 *
 * 本函数管**两类**：
 * 1. **三项文本必填** —— 保单号 / 保险公司 / 险种；
 * 2. **金额与期间的边界** —— 整数分 / ≥ 0 / 现金价值 ≤ 保额 / 起始日 ≤ 终止日。
 *
 * ⚠️ **当事人（投保人 / 被保人 / 受益人）的必填不在这里判** ——
 * `PolicyShapeInput` 里没有这三个字段，服务端 `InsuranceService.create()` 也**刻意只传
 * 与形态有关的字段**（见该文件注释：「`insureds` 不参与形态判定」）。它们的必填由
 * **契约层**（`contract/insurance.ts` 的 `PartyText` = `min(1)`）负责。
 *
 * 📌 **本文件的旧 docstring 曾声称"覆盖三人分离"**（与 `AC-B5.9` 并排引用）——
 * 那是**过度声明**：代码从未接收过当事人字段。㉝ 批做保单表单时按"必填到底由谁判"顺查发现，
 * 已订正（**只改文档，不改行为** —— 行为是正确的，错的是描述）。
 *
 * 🔴 **金额一律「整数分且 ≥ 0」**（规格 §22.1 禁浮点、§22.9 不用负数）——
 * 但这里是**资产估值**而非腿，故允许 `0`（未填与 0 都由 `null`/`0` 表达，不猜）。
 *
 * ⚠️ **`cashValue > sumAssured` 判为问题**：现金价值超过保额在真实保单里不成立，
 * 通常意味着**把"元"填成了"分"**或两个字段填反 —— 这正是值得拦的低级错。
 * 若确有罕见形态（如分红累积超过保额），**改本函数一处**即可，不要在服务端另写一份。
 */
export function checkPolicyShape(input: PolicyShapeInput): string[] {
  const issues: string[] = [];

  if (input.policyNo.trim() === '') issues.push('保单号必填');
  if (input.company.trim() === '') issues.push('保险公司必填（保单台账按公司名分组）');
  if (input.insuranceType.trim() === '') issues.push('险种必填');

  // 保险期间：起止都填时必须有序（只填一个允许：长期险常只填起始）
  if (input.startDate != null && input.endDate != null && input.startDate > input.endDate) {
    issues.push(`保险期间的起始日不得晚于终止日（${input.startDate} > ${input.endDate}）`);
  }

  for (const [label, value] of [
    ['保额', input.sumAssured],
    ['现金价值', input.cashValue],
    ['年缴保费', input.annualPremium],
  ] as const) {
    if (value === null || value === undefined) continue;
    if (!Number.isInteger(value)) issues.push(`${label}必须是**整数分**（禁浮点）`);
    else if (value < 0) issues.push(`${label}不得为负数`);
  }

  const sum = input.sumAssured ?? null;
  const cash = input.cashValue ?? null;
  if (sum !== null && cash !== null && sum >= 0 && cash > sum) {
    issues.push(
      `现金价值（${cash} 分）不得大于保额（${sum} 分）——`
        + '常见原因是把「元」当「分」填了，或两个字段填反',
    );
  }

  return issues;
}

// ═══════════════════════ 4. 社保类转账的形态（B5 · §5.5.1 / §5.5.3 / §5.5.4） ═══════════════════════

/**
 * 🔴 **社保「虚拟侧」（可用侧 · 3 个）** —— 电子虚拟账户。
 *
 * ⚠️ **不从注释分组推导**（`enums/account.ts` 明文「代码里禁止按那 3 组做分支；分组不是口径」）：
 * 口径在**净资产判定表**里逐值给出。虚拟侧的三个值同时满足两个**可判定**条件：
 * ① 在「可用型社保（计资产）」集合里（`networth.ts` 的 `SOCIAL_AVAILABLE`）；
 * ② **不是社保侧** —— 由命名规范判定：虚拟侧带 `_cur` / `_hist` / `_virtual` 后缀，
 *    社保侧**无后缀**（或用 `_sup` 表示补充类）。见 B 域 PRD §5.5.1 的命名定稿（`DR-021` §6.2.1）。
 *
 * ⇒ 这样写**不新增第三份清单**：集合成员资格取自净资产口径，侧别取自命名规范，
 * 两者都是别人已经定义过的权威事实。
 */
function isVirtualSideCategory(category: InternalCategory): boolean {
  if (!SOCIAL_AVAILABLE.includes(category)) return false;
  return (
    category === InternalCategory.SocialMedicalCur
    || category === InternalCategory.SocialMedicalHist
    || category === InternalCategory.SocialFundVirtual
  );
}

/**
 * **社保「社保侧」（登记与对账侧）** = 全部社保品类（净资产的可用 6 + 不计资产 3）**减去虚拟侧**。
 *
 * 两侧的判定是**互补**的（同一枚举的划分），故不会同时为真、也不会漏。
 */
function isSocialSideCategory(category: InternalCategory): boolean {
  const all = [...SOCIAL_AVAILABLE, ...SOCIAL_EXCLUDED];
  return all.includes(category) && !isVirtualSideCategory(category);
}

/**
 * 该品类**是不是社保侧的账户**（登记与对账侧 · 6 个）。
 *
 * 🔴 导出给**消费侧**用（C 域写入链校验"社保类转账两腿须不计收支"时的过滤）——
 * **不要在消费侧重写一份清单**（那就是铁律 24 的双实现）。
 */
export function isSocialSideAccount(category: string | null | undefined): boolean {
  if (category === null || category === undefined) return false;
  return isSocialSideCategory(category as InternalCategory);
}

/** 该品类**是不是社保虚拟侧（可用侧）的账户**（3 个）。同样导出给消费侧用。 */
export function isVirtualSideAccount(category: string | null | undefined): boolean {
  if (category === null || category === undefined) return false;
  return isVirtualSideCategory(category as InternalCategory);
}

/** 该品类是否属于**社保体系的任一侧**（社保侧 ∪ 虚拟侧）—— 消费侧判断"这笔是否涉及社保"用 */
export function isSocialAccount(category: string | null | undefined): boolean {
  return isSocialSideAccount(category) || isVirtualSideAccount(category);
}

/** 社保类转账校验所需的**一条腿**（最小子集 —— 品类由调用方 join 账户表得到） */
export interface SocialTransferLeg {
  /** 该腿所在账户的 `internal` 品类；非 `internal` 账户为 `null` */
  category: InternalCategory | null;
  direction: LegDirection;
}

/** 社保类转账的校验结论 */
export interface SocialTransferVerdict {
  issues: string[];
  /**
   * 社保侧账户在**这笔转账**里的进出形态：
   *
   * - `'settled'` —— 社保侧只有 `out`、虚拟侧只有 `in` ⇒ **转出式**（B 域 PRD §5.5.1 的前提）；
   * - `'partial'` —— 社保侧同时有 `out` 与 `in`（部分返还，或记错）；
   * - `'reverse'` —— 方向反了：虚拟侧 → 社保侧；
   * - `'n/a'` —— 不构成"社保侧 → 虚拟侧"（如两虚拟账户之间的「调整」）。
   */
  settlement: 'settled' | 'partial' | 'reverse' | 'n/a';
}

/**
 * 🔴 **社保类转账的形态校验**（B 域 PRD **§5.5.1** 前提 · §5.5.3 医保结转 · §5.5.4 公积金）——
 * 「社保侧 → 虚拟侧」的资金流动**必须两腿都不计收支**。
 *
 * ## 为什么这条必须有（不是多此一举）
 *
 * §5.5.1 给「医保计资产」加了一个**明文前提**：
 * > 「**「保费返还」须为转出式（社保侧归零），否则与虚拟侧重复计价**」
 *
 * 因为**两侧都计入净资产**：若这笔转账被记成普通收支（或漏了「不计收支」标记），
 * "钱从社保侧挪到虚拟侧"就会被当成**一笔支出 + 一笔收入**，净资产凭空变化；
 * 且两侧余额**同时存在**期间会被**重复计价**。
 *
 * ## 为什么判定放在**共享层**
 *
 * 「哪些品类算社保侧 / 虚拟侧」是**分类口径**，服务端校验与端侧表单联动都要同一答案；
 * 写成两份就是铁律 24 点名的双实现 ⇒ 本函数是**唯一判定处**，服务端经公开面注入调用。
 *
 * ## ⚠️ 本函数**不**判定的东西（诚实登记，别高估）
 *
 * - **金额是否等于社保侧余额**：那要读余额，而本函数是**纯函数**（没有库）。
 *   ⇒ 故"是否归零式"只作为 `settlement` **提示**返回，
 *   🔴 **不据此拦截** —— 部分返还当场硬拦会误伤真实场景（真的只返了一部分）；
 * - **是不是"返还"**：数据上与普通转账不可区分 ⇒ 本函数对所有"社保侧 ↔ 虚拟侧"的转账
 *   都要求两腿不计收支（那本来就是这类转账的**唯一正确记法**）；
 * - **不计收支标记本身**：由调用方在**腿级/整笔级**单独校验（本函数只看品类与方向）。
 */
export function checkSocialTransfer(legs: readonly SocialTransferLeg[]): SocialTransferVerdict {
  const socialLegs = legs.filter((l) => l.category !== null && isSocialSideCategory(l.category));
  const virtualLegs = legs.filter((l) => l.category !== null && isVirtualSideCategory(l.category));
  const issues: string[] = [];

  // 只有**跨侧**才管；同侧（如两虚拟账户之间的「调整」）不适用
  if (socialLegs.length === 0 || virtualLegs.length === 0) {
    return { issues, settlement: 'n/a' };
  }

  const socialOut = socialLegs.filter((l) => l.direction === 'out');
  const socialIn = socialLegs.filter((l) => l.direction === 'in');
  const virtualIn = virtualLegs.filter((l) => l.direction === 'in');
  const virtualOut = virtualLegs.filter((l) => l.direction === 'out');

  let settlement: SocialTransferVerdict['settlement'];
  if (socialOut.length > 0 && virtualIn.length > 0 && socialIn.length === 0) {
    settlement = 'settled';
  } else if (socialOut.length > 0 && socialIn.length > 0) {
    settlement = 'partial';
    issues.push(
      '社保侧账户同时有 out 与 in 腿 —— 「保费返还」是把钱**从社保侧挪到虚拟侧**，'
        + '同一笔里再给社保侧加钱属于**部分返还或记错了**；'
        + '若确实是部分返还，本笔只应记**实际挪出的金额**（留在社保侧的部分不进本笔）',
    );
  } else if (virtualOut.length > 0 && socialIn.length > 0) {
    settlement = 'reverse';
    issues.push(
      '方向反了：看起来是**虚拟侧 → 社保侧**。社保侧是「登记与对账」账户，'
        + '钱应从社保侧**流出**到虚拟侧（可用余额）—— 请检查两条腿的方向',
    );
  } else {
    settlement = 'n/a';
  }

  return { issues, settlement };
}

